/**
 * Unified Point Types Configuration
 *
 * This file provides a single source of truth for point type definitions
 * used across TemplateEditor.vue, PointsManager.vue, and ControllerAssignment.vue.
 *
 * Point Type Structure:
 * - io_type: Physical I/O type (AI, AO, DI, DO, Virtual)
 * - semantic_type: Human-readable name for UI display
 * - value_type: Data value type (analog, binary, multistate)
 * - brick_class: Default BRICK Schema class
 * - category: Grouping category for dropdowns
 *
 * Usage:
 *   import { POINT_TYPES, getPointTypesByCategory, getPointTypeByName } from '@/config/pointTypes'
 */

// =============================================================================
// Constants
// =============================================================================

/**
 * I/O Types for controller assignment
 */
export const IO_TYPES = ['AI', 'AO', 'DI', 'DO', 'Virtual'];

/**
 * Value types for points
 */
export const VALUE_TYPES = ['analog', 'binary', 'multistate'];

/**
 * Categories for grouping points in dropdowns
 */
export const CATEGORIES = ['Sensors', 'Setpoints', 'Commands', 'Status', 'I/O'];

// =============================================================================
// Point Type Definitions
// =============================================================================

/**
 * Complete point type definitions with all properties
 */
export const POINT_TYPES = {
  // =========================================================================
  // SENSORS (Physical Inputs - AI/DI)
  // =========================================================================

  // Analog Sensors (AI)
  'Temperature': {
    io_type: 'AI',
    semantic_type: 'Temperature',
    value_type: 'analog',
    brick_class: 'brick:Temperature_Sensor',
    category: 'Sensors',
    unit: 'DEG_C',
    description: 'Temperature measurement sensor',
  },
  'Humidity': {
    io_type: 'AI',
    semantic_type: 'Humidity',
    value_type: 'analog',
    brick_class: 'brick:Humidity_Sensor',
    category: 'Sensors',
    unit: 'PERCENT_RH',
    description: 'Relative humidity sensor',
  },
  'Pressure': {
    io_type: 'AI',
    semantic_type: 'Pressure',
    value_type: 'analog',
    brick_class: 'brick:Pressure_Sensor',
    category: 'Sensors',
    unit: 'PA',
    description: 'Pressure measurement sensor',
  },
  'Flow': {
    io_type: 'AI',
    semantic_type: 'Flow',
    value_type: 'analog',
    brick_class: 'brick:Flow_Sensor',
    category: 'Sensors',
    unit: 'L-PER-SEC',
    description: 'Flow rate sensor',
  },
  'CO2': {
    io_type: 'AI',
    semantic_type: 'CO2',
    value_type: 'analog',
    brick_class: 'brick:CO2_Sensor',
    category: 'Sensors',
    unit: 'PPM',
    description: 'Carbon dioxide concentration sensor',
  },
  'Light Level': {
    io_type: 'AI',
    semantic_type: 'Light Level',
    value_type: 'analog',
    brick_class: 'brick:Luminance_Sensor',
    category: 'Sensors',
    unit: 'LUX',
    description: 'Light level / luminance sensor',
  },
  'Power': {
    io_type: 'AI',
    semantic_type: 'Power',
    value_type: 'analog',
    brick_class: 'brick:Power_Sensor',
    category: 'Sensors',
    unit: 'KiloW',
    description: 'Electrical power sensor',
  },
  'Energy': {
    io_type: 'AI',
    semantic_type: 'Energy',
    value_type: 'analog',
    brick_class: 'brick:Energy_Sensor',
    category: 'Sensors',
    unit: 'KiloW-HR',
    description: 'Energy consumption sensor',
  },
  'Current': {
    io_type: 'AI',
    semantic_type: 'Current',
    value_type: 'analog',
    brick_class: 'brick:Current_Sensor',
    category: 'Sensors',
    unit: 'A',
    description: 'Electrical current sensor',
  },
  'Voltage': {
    io_type: 'AI',
    semantic_type: 'Voltage',
    value_type: 'analog',
    brick_class: 'brick:Voltage_Sensor',
    category: 'Sensors',
    unit: 'V',
    description: 'Electrical voltage sensor',
  },

  // Multi-Sensors (AI) - devices that measure multiple parameters
  'Temperature & Humidity': {
    io_type: 'AI',
    semantic_type: 'Temperature & Humidity',
    value_type: 'analog',
    brick_class: 'brick:Sensor',
    category: 'Sensors',
    unit: 'DEG_C',
    description: 'Combined temperature and humidity sensor (e.g., DHT22, BME280)',
    multi_sensor: true,
    measures: ['temperature', 'humidity'],
  },
  'Temperature, Humidity & CO2': {
    io_type: 'AI',
    semantic_type: 'Temperature, Humidity & CO2',
    value_type: 'analog',
    brick_class: 'brick:Sensor',
    category: 'Sensors',
    unit: 'PPM',
    description: 'Indoor air quality sensor measuring temperature, humidity, and CO2',
    multi_sensor: true,
    measures: ['temperature', 'humidity', 'co2'],
  },
  'Air Quality': {
    io_type: 'AI',
    semantic_type: 'Air Quality',
    value_type: 'analog',
    brick_class: 'brick:Air_Quality_Sensor',
    category: 'Sensors',
    unit: 'PPM',
    description: 'Air quality sensor (VOC, particulates, etc.)',
  },
  'Differential Pressure': {
    io_type: 'AI',
    semantic_type: 'Differential Pressure',
    value_type: 'analog',
    brick_class: 'brick:Differential_Pressure_Sensor',
    category: 'Sensors',
    unit: 'PA',
    description: 'Differential pressure sensor for filter status, duct pressure',
  },

  // Binary Sensors (DI)
  'Occupancy': {
    io_type: 'DI',
    semantic_type: 'Occupancy',
    value_type: 'binary',
    brick_class: 'brick:Occupancy_Sensor',
    category: 'Sensors',
    description: 'Space occupancy sensor',
  },
  'Motion': {
    io_type: 'DI',
    semantic_type: 'Motion',
    value_type: 'binary',
    brick_class: 'brick:Motion_Sensor',
    category: 'Sensors',
    description: 'Motion detection sensor',
  },

  // =========================================================================
  // SETPOINTS (Virtual - Software values)
  // =========================================================================

  'Temperature Setpoint': {
    io_type: 'Virtual',
    semantic_type: 'Temperature Setpoint',
    value_type: 'analog',
    brick_class: 'brick:Temperature_Setpoint',
    category: 'Setpoints',
    unit: 'DEG_C',
    description: 'Temperature setpoint value',
  },
  'Pressure Setpoint': {
    io_type: 'Virtual',
    semantic_type: 'Pressure Setpoint',
    value_type: 'analog',
    brick_class: 'brick:Pressure_Setpoint',
    category: 'Setpoints',
    unit: 'PA',
    description: 'Pressure setpoint value',
  },
  'Flow Setpoint': {
    io_type: 'Virtual',
    semantic_type: 'Flow Setpoint',
    value_type: 'analog',
    brick_class: 'brick:Flow_Setpoint',
    category: 'Setpoints',
    unit: 'L-PER-SEC',
    description: 'Flow rate setpoint value',
  },
  'Humidity Setpoint': {
    io_type: 'Virtual',
    semantic_type: 'Humidity Setpoint',
    value_type: 'analog',
    brick_class: 'brick:Humidity_Setpoint',
    category: 'Setpoints',
    unit: 'PERCENT_RH',
    description: 'Humidity setpoint value',
  },
  'CO2 Setpoint': {
    io_type: 'Virtual',
    semantic_type: 'CO2 Setpoint',
    value_type: 'analog',
    brick_class: 'brick:CO2_Setpoint',
    category: 'Setpoints',
    unit: 'PPM',
    description: 'CO2 concentration setpoint value',
  },

  // =========================================================================
  // COMMANDS (Physical Outputs - DO/AO)
  // =========================================================================

  // Binary Commands (DO)
  'Start/Stop': {
    io_type: 'DO',
    semantic_type: 'Start/Stop',
    value_type: 'binary',
    brick_class: 'brick:Start_Stop_Command',
    category: 'Commands',
    description: 'Start/Stop command for equipment',
  },
  'Enable/Disable': {
    io_type: 'DO',
    semantic_type: 'Enable/Disable',
    value_type: 'binary',
    brick_class: 'brick:Enable_Command',
    category: 'Commands',
    description: 'Enable/Disable command',
  },
  'On/Off': {
    io_type: 'DO',
    semantic_type: 'On/Off',
    value_type: 'binary',
    brick_class: 'brick:On_Off_Command',
    category: 'Commands',
    description: 'On/Off command',
  },
  'Open/Close': {
    io_type: 'DO',
    semantic_type: 'Open/Close',
    value_type: 'binary',
    brick_class: 'brick:Position_Command',
    category: 'Commands',
    description: 'Open/Close position command',
  },
  'Reset': {
    io_type: 'DO',
    semantic_type: 'Reset',
    value_type: 'binary',
    brick_class: 'brick:Reset_Command',
    category: 'Commands',
    description: 'Reset command',
  },

  // Analog Commands (AO)
  'Damper Command': {
    io_type: 'AO',
    semantic_type: 'Damper Command',
    value_type: 'analog',
    brick_class: 'brick:Damper_Position_Command',
    category: 'Commands',
    unit: 'PERCENT',
    description: 'Damper position command (0-100%)',
  },
  'Valve Command': {
    io_type: 'AO',
    semantic_type: 'Valve Command',
    value_type: 'analog',
    brick_class: 'brick:Valve_Position_Command',
    category: 'Commands',
    unit: 'PERCENT',
    description: 'Valve position command (0-100%)',
  },
  'Speed Command': {
    io_type: 'AO',
    semantic_type: 'Speed Command',
    value_type: 'analog',
    brick_class: 'brick:Speed_Command',
    category: 'Commands',
    unit: 'PERCENT',
    description: 'Speed/frequency command (0-100%)',
  },

  // =========================================================================
  // STATUS (Physical Inputs - DI / Analog - AI / Virtual)
  // =========================================================================

  'Running Status': {
    io_type: 'DI',
    semantic_type: 'Running Status',
    value_type: 'binary',
    brick_class: 'brick:Run_Status',
    category: 'Status',
    description: 'Equipment running status',
  },
  'Alarm Status': {
    io_type: 'DI',
    semantic_type: 'Alarm Status',
    value_type: 'binary',
    brick_class: 'brick:Alarm',
    category: 'Status',
    description: 'Alarm status indicator',
  },
  'Fault Status': {
    io_type: 'DI',
    semantic_type: 'Fault Status',
    value_type: 'binary',
    brick_class: 'brick:Fault_Status',
    category: 'Status',
    description: 'Fault status indicator',
  },
  'Mode Status': {
    io_type: 'Virtual',
    semantic_type: 'Mode Status',
    value_type: 'multistate',
    brick_class: 'brick:Mode_Status',
    category: 'Status',
    description: 'Operating mode status (multistate)',
  },
  'Position Status': {
    io_type: 'AI',
    semantic_type: 'Position Status',
    value_type: 'analog',
    brick_class: 'brick:Position_Sensor',
    category: 'Status',
    unit: 'PERCENT',
    description: 'Position feedback (0-100%)',
  },

  // =========================================================================
  // GENERIC I/O (For manual/custom mapping)
  // =========================================================================

  'Analog Input': {
    io_type: 'AI',
    semantic_type: 'Analog Input',
    value_type: 'analog',
    brick_class: 'brick:Point',
    category: 'I/O',
    description: 'Generic analog input',
  },
  'Analog Output': {
    io_type: 'AO',
    semantic_type: 'Analog Output',
    value_type: 'analog',
    brick_class: 'brick:Point',
    category: 'I/O',
    description: 'Generic analog output',
  },
  'Binary Input': {
    io_type: 'DI',
    semantic_type: 'Binary Input',
    value_type: 'binary',
    brick_class: 'brick:Point',
    category: 'I/O',
    description: 'Generic binary input',
  },
  'Binary Output': {
    io_type: 'DO',
    semantic_type: 'Binary Output',
    value_type: 'binary',
    brick_class: 'brick:Point',
    category: 'I/O',
    description: 'Generic binary output',
  },
  'Multistate Input': {
    io_type: 'AI',
    semantic_type: 'Multistate Input',
    value_type: 'multistate',
    brick_class: 'brick:Point',
    category: 'I/O',
    description: 'Generic multistate input',
  },
  'Multistate Output': {
    io_type: 'AO',
    semantic_type: 'Multistate Output',
    value_type: 'multistate',
    brick_class: 'brick:Point',
    category: 'I/O',
    description: 'Generic multistate output',
  },
};

// =============================================================================
// QUDT Unit Labels for Display
// =============================================================================

/**
 * QUDT unit code to display label mapping
 * These map standard QUDT unit codes to human-readable display strings
 */
export const UNIT_LABELS = {
  'DEG_C': '\u00B0C',
  'DEG_F': '\u00B0F',
  'K': 'K',
  'PERCENT': '%',
  'PERCENT_RH': '%RH',
  'PA': 'Pa',
  'KiloPA': 'kPa',
  'PSI': 'psi',
  'BAR': 'bar',
  'L-PER-SEC': 'L/s',
  'M3-PER-SEC': 'm\u00B3/s',
  'CFM': 'CFM',
  'PPM': 'ppm',
  'LUX': 'lux',
  'KiloW': 'kW',
  'W': 'W',
  'KiloW-HR': 'kWh',
  'A': 'A',
  'V': 'V',
  'HZ': 'Hz',
  'REV-PER-MIN': 'RPM',
};

/**
 * Get display label for a QUDT unit code
 *
 * @param {string} qudtCode - QUDT unit code (e.g., 'DEG_C', 'PERCENT')
 * @returns {string} Human-readable unit label (e.g., '\u00B0C', '%')
 *
 * @example
 * getUnitLabel('DEG_C') // Returns: '\u00B0C'
 * getUnitLabel('PERCENT') // Returns: '%'
 * getUnitLabel('UNKNOWN') // Returns: 'UNKNOWN' (fallback)
 */
export function getUnitLabel(qudtCode) {
  return UNIT_LABELS[qudtCode] || qudtCode;
}

// =============================================================================
// I/O Type Display Configuration
// =============================================================================

/**
 * I/O type labels for display
 */
export const IO_TYPE_LABELS = {
  'AI': 'Analog Input',
  'AO': 'Analog Output',
  'DI': 'Digital Input',
  'DO': 'Digital Output',
  'Virtual': 'Virtual Point',
};

/**
 * I/O type colors for badges
 */
export const IO_TYPE_COLORS = {
  'AI': 'blue',
  'AO': 'orange',
  'DI': 'green',
  'DO': 'purple',
  'Virtual': 'grey',
};

/**
 * Value type colors for badges
 */
export const VALUE_TYPE_COLORS = {
  'analog': 'blue',
  'binary': 'green',
  'multistate': 'orange',
};

/**
 * Category colors for badges and visual grouping
 */
export const CATEGORY_COLORS = {
  'Sensors': 'blue',
  'Setpoints': 'cyan',
  'Commands': 'purple',
  'Status': 'green',
  'I/O': 'grey',
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get all point types grouped by category
 *
 * @returns {Object} Point types organized by category
 *
 * @example
 * const grouped = getPointTypesByCategory();
 * // Returns: { Sensors: [...], Setpoints: [...], Commands: [...], Status: [...], 'I/O': [...] }
 */
export function getPointTypesByCategory() {
  const grouped = {};

  // Initialize categories in order
  CATEGORIES.forEach((category) => {
    grouped[category] = [];
  });

  // Group point types by category
  Object.entries(POINT_TYPES).forEach(([name, config]) => {
    if (grouped[config.category]) {
      grouped[config.category].push({
        name,
        label: config.semantic_type,
        value: name,
        ...config,
      });
    }
  });

  return grouped;
}

/**
 * Get point type configuration by name
 *
 * @param {string} name - Point type name (e.g., 'Temperature', 'Start/Stop')
 * @returns {Object|null} Point type configuration or null if not found
 *
 * @example
 * const tempConfig = getPointTypeByName('Temperature');
 * // Returns: { io_type: 'AI', semantic_type: 'Temperature', value_type: 'analog', ... }
 */
export function getPointTypeByName(name) {
  return POINT_TYPES[name] || null;
}

/**
 * Get display label for an I/O type
 *
 * @param {string} ioType - I/O type code (AI, AO, DI, DO, Virtual)
 * @returns {string} Human-readable label
 *
 * @example
 * getIoTypeLabel('AI') // Returns: 'Analog Input'
 */
export function getIoTypeLabel(ioType) {
  return IO_TYPE_LABELS[ioType] || ioType;
}

/**
 * Get color for I/O type badge
 *
 * @param {string} ioType - I/O type code (AI, AO, DI, DO, Virtual)
 * @returns {string} Quasar color name
 *
 * @example
 * getIoTypeColor('AI') // Returns: 'blue'
 */
export function getIoTypeColor(ioType) {
  return IO_TYPE_COLORS[ioType] || 'grey';
}

/**
 * Get color for value type badge
 *
 * @param {string} valueType - Value type (analog, binary, multistate)
 * @returns {string} Quasar color name
 *
 * @example
 * getValueTypeColor('analog') // Returns: 'blue'
 */
export function getValueTypeColor(valueType) {
  return VALUE_TYPE_COLORS[valueType] || 'grey';
}

/**
 * Get color for category badge
 *
 * @param {string} category - Category name
 * @returns {string} Quasar color name
 *
 * @example
 * getCategoryColor('Sensors') // Returns: 'blue'
 */
export function getCategoryColor(category) {
  return CATEGORY_COLORS[category] || 'grey';
}

/**
 * Check if an I/O type represents a physical I/O point (not virtual)
 *
 * @param {string} ioType - I/O type code
 * @returns {boolean} True if physical I/O (AI, AO, DI, DO), false if Virtual
 *
 * @example
 * isPhysicalIo('AI') // Returns: true
 * isPhysicalIo('Virtual') // Returns: false
 */
export function isPhysicalIo(ioType) {
  return ['AI', 'AO', 'DI', 'DO'].includes(ioType);
}

/**
 * Check if an I/O type is an input type
 *
 * @param {string} ioType - I/O type code
 * @returns {boolean} True if input type (AI, DI)
 */
export function isInputType(ioType) {
  return ['AI', 'DI'].includes(ioType);
}

/**
 * Check if an I/O type is an output type
 *
 * @param {string} ioType - I/O type code
 * @returns {boolean} True if output type (AO, DO)
 */
export function isOutputType(ioType) {
  return ['AO', 'DO'].includes(ioType);
}

/**
 * Get point types by I/O type
 *
 * @param {string} ioType - I/O type code
 * @returns {Array} Array of point type configurations
 *
 * @example
 * getPointTypesByIoType('AI') // Returns all AI point types
 */
export function getPointTypesByIoType(ioType) {
  return Object.entries(POINT_TYPES)
    .filter(([, config]) => config.io_type === ioType)
    .map(([name, config]) => ({
      name,
      label: config.semantic_type,
      value: name,
      ...config,
    }));
}

/**
 * Get point types by value type
 *
 * @param {string} valueType - Value type (analog, binary, multistate)
 * @returns {Array} Array of point type configurations
 *
 * @example
 * getPointTypesByValueType('binary') // Returns all binary point types
 */
export function getPointTypesByValueType(valueType) {
  return Object.entries(POINT_TYPES)
    .filter(([, config]) => config.value_type === valueType)
    .map(([name, config]) => ({
      name,
      label: config.semantic_type,
      value: name,
      ...config,
    }));
}

/**
 * Get all point type names as a flat array
 *
 * @returns {Array<string>} Array of point type names
 *
 * @example
 * getPointTypeNames() // Returns: ['Temperature', 'Humidity', 'Pressure', ...]
 */
export function getPointTypeNames() {
  return Object.keys(POINT_TYPES);
}

/**
 * Get point types as dropdown options (for q-select)
 *
 * @param {Object} options - Filter options
 * @param {string} [options.category] - Filter by category
 * @param {string} [options.ioType] - Filter by I/O type
 * @param {string} [options.valueType] - Filter by value type
 * @returns {Array} Array of { label, value } objects for q-select
 *
 * @example
 * getPointTypeOptions({ category: 'Sensors' })
 * // Returns: [{ label: 'Temperature', value: 'Temperature' }, ...]
 */
export function getPointTypeOptions(options = {}) {
  let entries = Object.entries(POINT_TYPES);

  // Apply filters
  if (options.category) {
    entries = entries.filter(([, config]) => config.category === options.category);
  }
  if (options.ioType) {
    entries = entries.filter(([, config]) => config.io_type === options.ioType);
  }
  if (options.valueType) {
    entries = entries.filter(([, config]) => config.value_type === options.valueType);
  }

  return entries.map(([name, config]) => ({
    label: config.semantic_type,
    value: name,
  }));
}

/**
 * Get grouped options for q-select with option groups
 *
 * @returns {Array} Array of grouped options for q-select
 *
 * @example
 * // Use in q-select with option-group-label
 * const options = getGroupedPointTypeOptions();
 */
export function getGroupedPointTypeOptions() {
  const options = [];
  const grouped = getPointTypesByCategory();

  Object.entries(grouped).forEach(([category, types]) => {
    if (types.length > 0) {
      // Add category header (disabled option)
      options.push({
        label: category,
        value: `__category_${category}`,
        disable: true,
        category: true,
      });

      // Add types in this category
      types.forEach((type) => {
        options.push({
          label: `  ${type.semantic_type}`,
          value: type.name,
          category: category,
          ioType: type.io_type,
          valueType: type.value_type,
        });
      });
    }
  });

  return options;
}

/**
 * Get default BRICK class for a point type name
 *
 * @param {string} pointTypeName - Point type name
 * @returns {string} BRICK class or 'brick:Point' as fallback
 */
export function getDefaultBrickClass(pointTypeName) {
  const config = POINT_TYPES[pointTypeName];
  return config?.brick_class || 'brick:Point';
}

/**
 * Get default I/O type for a point type name
 *
 * @param {string} pointTypeName - Point type name
 * @returns {string} I/O type or 'Virtual' as fallback
 */
export function getDefaultIoType(pointTypeName) {
  const config = POINT_TYPES[pointTypeName];
  return config?.io_type || 'Virtual';
}

/**
 * Get default value type for a point type name
 *
 * @param {string} pointTypeName - Point type name
 * @returns {string} Value type or 'analog' as fallback
 */
export function getDefaultValueType(pointTypeName) {
  const config = POINT_TYPES[pointTypeName];
  return config?.value_type || 'analog';
}

/**
 * Calculate I/O counts from an array of points
 *
 * @param {Array} points - Array of point objects with point_type property
 * @returns {Object} I/O counts { AI, AO, DI, DO, Virtual, total }
 *
 * @example
 * const counts = calculateIoCounts([{ point_type: 'Temperature' }, { point_type: 'Start/Stop' }])
 * // Returns: { AI: 1, AO: 0, DI: 0, DO: 1, Virtual: 0, total: 2 }
 */
export function calculateIoCounts(points) {
  const counts = {
    AI: 0,
    AO: 0,
    DI: 0,
    DO: 0,
    Virtual: 0,
    total: 0,
  };

  if (!Array.isArray(points)) {
    return counts;
  }

  points.forEach((point) => {
    const pointType = point.point_type || point.name;
    const config = POINT_TYPES[pointType];

    if (config) {
      counts[config.io_type]++;
      counts.total++;
    }
  });

  return counts;
}

// =============================================================================
// Default Export
// =============================================================================

export default {
  // Constants
  IO_TYPES,
  VALUE_TYPES,
  CATEGORIES,
  POINT_TYPES,
  UNIT_LABELS,
  IO_TYPE_LABELS,
  IO_TYPE_COLORS,
  VALUE_TYPE_COLORS,
  CATEGORY_COLORS,

  // Functions
  getPointTypesByCategory,
  getPointTypeByName,
  getUnitLabel,
  getIoTypeLabel,
  getIoTypeColor,
  getValueTypeColor,
  getCategoryColor,
  isPhysicalIo,
  isInputType,
  isOutputType,
  getPointTypesByIoType,
  getPointTypesByValueType,
  getPointTypeNames,
  getPointTypeOptions,
  getGroupedPointTypeOptions,
  getDefaultBrickClass,
  getDefaultIoType,
  getDefaultValueType,
  calculateIoCounts,
};
